import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  // const [searchParams] = useSearchParams(); // Not used
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      
      // Check for OAuth errors in URL
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const oauthError = urlParams.get('error') || hashParams.get('error');
      const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');
      
      if (oauthError) {
        
        if (errorDescription?.includes('Database error saving new user')) {
          
          setError('Google sign-in is experiencing technical difficulties with new account creation. Please create an account using the regular signup form with your Google email address. Once your account exists, Google sign-in should work normally.');
          setLoading(false);
          return;
        } else {
          setError(`Google sign-in failed: ${errorDescription || oauthError}`);
        }
      } else {
        // No error in URL, check if we have a session
        try {
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.user) {
            navigate('/dashboard');
            return;
          } else {
            // Wait a bit for auth state to update
            setTimeout(() => {
              if (user) {
                navigate('/dashboard');
              } else {
                setError('Authentication failed. Please try again.');
              }
            }, 2000);
          }
        } catch (sessionError) {
          console.error('🚨 OAuthCallback: Error checking session:', sessionError);
          setError('Authentication failed. Please try again.');
        }
      }
      
      setLoading(false);
    };

    handleOAuthCallback();
  }, [navigate, user]);

  // If user is authenticated, redirect to dashboard
  useEffect(() => {
    if (!authLoading && user) {
      console.log('OAuthCallback: User authenticated, redirecting to dashboard');
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  const handleRetry = () => {
    navigate('/login');
  };

  if (loading || authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <Container maxWidth="sm">
          <Box textAlign="center" color="white">
            <CircularProgress size={48} sx={{ mb: 2, color: 'white' }} />
            <Typography variant="h6">
              Completing Google sign-in...
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              Please wait while we set up your account.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Box textAlign="center">
            <Button 
              variant="contained" 
              onClick={handleRetry}
              sx={{ mr: 1 }}
            >
              Try Again
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/register')}
            >
              Sign Up with Email
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return null;
};

export default OAuthCallback;